import { useQuery } from '@apollo/client'
import { NoticeLayout } from '@components/layouts/notice-layout'
import { BaseBlock } from '@components/base-block'
import { LabelM, LabelXS } from '@components/base-typography'
import { Icon, IconType } from '@components/icon'
import { Pagination } from '@components/pagination'
import {
  GetNoticesByOffsetDocument,
  NoticeSortBy,
  NoticeType,
} from '@lib/graphql/documents.generated'
import { usePagination } from '@lib/hooks/use-pagination'
import { Routes } from '@lib/routes'
import { dateFormat } from '@lib/utils/date-format'
import { generatePath } from '@lib/utils/generate-path'
import { colors } from '@styles/colors'
import { ellipsis } from '@styles/ellipsis'
import { borderRadius, transition } from '@styles/utils'
import { NextPage } from 'next'
import Link from 'next/link'
import { padding } from 'polished'
import { useStyletron } from 'styletron-react'

const PAGE_LIMIT = 12

interface NoticePageProps {}

const NoticePage: NextPage<NoticePageProps> = () => {
  const [css] = useStyletron()

  const { paginationProps, setCountTotal, limit, offset } =
    usePagination(PAGE_LIMIT)

  const { data, loading } = useQuery(GetNoticesByOffsetDocument, {
    variables: {
      offset,
      limit,
      sortBy: NoticeSortBy.CreatedAtDesc,
    },
    onCompleted: ({ notices: { pageInfo } }) => {
      if (pageInfo) {
        setCountTotal(pageInfo.countTotal)
      }
    },
  })
  const notices = data?.notices?.edges || []

  return (
    <BaseBlock marginTop="40px">
      {!loading &&
        notices.map(({ node: { title, type, id, createdAt } }) => (
          <Link
            href={generatePath(Routes.SUPPORT_NOTICE_DETAIL, {
              id: id,
            })}
            passHref
            key={id}
          >
            <BaseBlock
              as="a"
              className={css({
                display: 'flex',
                alignItems: 'center',
                columnGap: '16px',
                cursor: 'pointer',
                width: '100%',
                boxSizing: 'border-box',
                ...padding('20px', '8px'),
                ...transition('background-color', '0.2s'),
                ...borderRadius(8),
                ':hover': {
                  backgroundColor: colors.gray50,
                },
              })}
            >
              <Icon
                type={
                  {
                    [NoticeType.Notice]: 'MegaphoneLoud',
                    [NoticeType.Event]: 'CalendarCheckmark',
                    [NoticeType.News]: 'News',
                  }[type] as IconType
                }
                size={20}
                color={colors.gray600}
              />
              <LabelM
                flex="1 1 auto"
                $style={{
                  ...ellipsis(1),
                }}
              >
                {title}
              </LabelM>
              <LabelXS color={colors.gray400}>
                {dateFormat(createdAt, 'yyyy.MM.dd')}
              </LabelXS>
            </BaseBlock>
          </Link>
        ))}
      {!loading && (
        <Pagination
          className={css({
            marginTop: '32px',
          })}
          {...paginationProps}
        />
      )}
    </BaseBlock>
  )
}

NoticePage.getLayout = (page) => (
  <NoticeLayout helmetTitle="Ontol | 공지사항">{page}</NoticeLayout>
)

export default NoticePage
